import React from 'react';
import {
    Container, Typography, Box, Paper, Divider,
    Select, MenuItem, FormControl, InputLabel,
    TextField, Button, Stack, Grid, Switch, Checkbox,
    FormControlLabel, TextareaAutosize
} from '@mui/material';


import TimeInput from '../../../../../components/TimeInput';
import useConfigForm from '../../../../../hooks/useConfigForm';
import useSendConfig from '../../../../../hooks/useSendConfig';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';



const ZONAS = Array.from({ length: 8 }, (_, i) => ({ value: i, label: `Zona ${i + 1}` }));

const TIPO_CHAPAS = [
    { value: 0, label: 'Electrica' },
    { value: 1, label: 'Magnetica' }
];

export const generarEstadoInicialAcceso = () => {
    return Array.from({ length: 2 }, (_, i) => ({
        nAcceso: i,
        hab: 0,
        zonaAsociada: i,
        tiempoParaAbrir: 90,
        tiempoParaCerrar: 300,
        intervaloNotificaciones: 300,
        tipoChapa: 0,
        tiempoPulso: 5,
        desc: "---"
    }));
};

function ConfiguracionAccesoForm({
    config,
    setConfig,
    state,
    setState,
    originalConfigRef
}) {

    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();

    const {
        accesoSeleccionado,
        jsonOutput,
        formDirty,
        jsonNeedsUpdate
    } = state;

    const validarZonasAsociadas = (config) => {
        //console.log("Validando zonas!");
        let zonasUsadas = [];
        for (const acceso of config) {
            if (acceso.hab) {
                if (zonasUsadas.includes(acceso.zonaAsociada)) {
                    return "No puede asociarse una misma zona a más de un acceso habilitado.";
                } else {
                    zonasUsadas.push(acceso.zonaAsociada);
                }
            }
        }
        //console.log("zonasUsadas: ", zonasUsadas);
        return null;
    };

    // Hook para el envío de los datos
    const { sendConfig } = useSendConfig({
        command: "CMD_SET_CONFIG_ACCESO",
        config,
        setState,
        originalConfigRef,
        currentUser,
        device,
        dispatch,
        startLoading,
        endLoading,
        extraValidation: validarZonasAsociadas
    });

    // 🔥 Hook reutilizable
    const {
        handleGenerateJson,
        handleSend,
        handleCancel
    } = useConfigForm({
        config,
        setConfig,
        state,
        setState,
        originalConfigRef,
        onSend: sendConfig
    });


    const current = config?.[accesoSeleccionado];
    if (!current) return null;

    // -------------------------------
    // HANDLERS
    // -------------------------------
    const handleAccessChange = (e) => {
        setState(prev => ({
            ...prev,
            accesoSeleccionado: Number(e.target.value)
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        const val = type === 'checkbox' ? (checked ? 1 : 0) : value;

        const newConfig = config.map((p, i) =>
            i === accesoSeleccionado ? { ...p, [name]: val } : p
        );

        setConfig(newConfig);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5">
                    Configuración de Control de Acceso
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>

                    {/* IZQUIERDA */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>

                            <Typography
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase'
                                }}
                            >
                                CONTROL DE ACCESO
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Acceso</InputLabel>
                                <Select
                                    value={accesoSeleccionado}
                                    onChange={handleAccessChange}
                                    label="Acceso"
                                >
                                    <MenuItem value={0}>Acceso 1</MenuItem>
                                    <MenuItem value={1}>Acceso 2</MenuItem>
                                </Select>
                            </FormControl>

                            <Grid item xs={12}> <FormControlLabel control={<Switch name="hab" checked={!!current.hab} onChange={handleChange} />} label="Habilitar Acceso" /> </Grid>

                            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
                                <InputLabel>Zona Asociada</InputLabel>
                                <Select
                                    name="zonaAsociada"
                                    value={current.zonaAsociada}
                                    onChange={handleChange}
                                    disabled={!current.hab}
                                    label="Zona Asociada"
                                >
                                    {ZONAS.map(z => (
                                        <MenuItem key={z.value} value={z.value}>
                                            {z.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* 🔥 TimeInput PRO */}
                            <TimeInput
                                label="Tiempo Para Abrir"
                                name="tiempoParaAbrir"
                                value={current.tiempoParaAbrir}
                                mode="ms"
                                min={10}
                                max={86400}
                                onChange={handleChange}
                            />

                            <TimeInput
                                label="Tiempo Para Cerrar"
                                name="tiempoParaCerrar"
                                value={current.tiempoParaCerrar}
                                mode="ms"
                                min={10}
                                max={86400}
                                onChange={handleChange}
                            />

                            <TimeInput
                                label="Intervalo Notificaciones"
                                name="intervaloNotificaciones"
                                value={current.intervaloNotificaciones}
                                mode="ms"
                                min={10}
                                max={86400}
                                onChange={handleChange}
                            />

                            <TimeInput
                                label="Tiempo Pulso"
                                name="tiempoPulso"
                                value={current.tiempoPulso}
                                mode="s"
                                min={1}
                                max={15}
                                onChange={handleChange}
                            />

                            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                                <InputLabel>Tipo Chapa</InputLabel>
                                <Select
                                    name="tipoChapa"
                                    value={current.tipoChapa}
                                    onChange={handleChange}
                                    label="Tipo Chapa"
                                >
                                    {TIPO_CHAPAS.map(t => (
                                        <MenuItem key={t.value} value={t.value}>
                                            {t.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Descripción"
                                name="desc"
                                value={current.desc}
                                onChange={handleChange}
                                inputProps={{ maxLength: 8 }}
                                sx={{ mt: 2 }}
                            />
                        </Paper>
                    </Grid>

                    {/* DERECHA */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >

                        {/* JSON */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                pt: 4
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase'
                                }}
                            >
                                JSON DE CONFIGURACIÓN
                            </Typography>


                            {jsonOutput ? (
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                    <TextareaAutosize
                                        value={jsonOutput}
                                        minRows={10}
                                        maxRows={36}
                                        style={{
                                            width: '100%',
                                            border: `1px solid ${jsonNeedsUpdate ? '#ff9800' : '#ccc'}`,
                                            borderRadius: '4px',
                                            padding: '8px',
                                            fontFamily: 'monospace',
                                            resize: 'vertical',
                                            backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'white'
                                        }}
                                        readOnly
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography color="text.secondary">
                                        Genera el JSON para previsualizarlo aquí.
                                    </Typography>
                                </Box>
                            )}


                            <Button
                                variant="contained"
                                color={jsonNeedsUpdate ? "warning" : "primary"}
                                onClick={handleGenerateJson}
                            >
                                {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                            </Button>
                        </Paper>

                        {/* RESUMEN */}
                        <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                            <Typography variant="h6">Resumen</Typography>

                            <Typography>Acceso: {accesoSeleccionado + 1}</Typography>
                            <Typography>Habilitada: {current.hab ? 'Sí' : 'No'}</Typography>
                            <Typography>Zona: {current.zonaAsociada + 1}</Typography>
                            <Typography>
                                Tipo Chapa: {TIPO_CHAPAS.find(t => t.value === current.tipoChapa)?.label}
                            </Typography>
                            <Typography>Tiempo Abrir: {current.tiempoParaAbrir}s</Typography>
                            <Typography>Descripción: {current.desc}</Typography>

                            <Box mt={2}>
                                <Typography color={formDirty ? 'error' : 'success.main'}>
                                    Formulario Modificado: {formDirty ? 'Sí' : 'No'}
                                </Typography>

                                <Typography color={jsonNeedsUpdate ? 'warning.main' : 'success.main'}>
                                    JSON Actualizado: {jsonNeedsUpdate ? 'No' : 'Sí'}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* RESULTADO ENVÍO */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                RESULTADO ENVÍO AL DISPOSITIVO
                            </Typography>

                            <Typography sx={{ mt: 1 }}>
                                ConfigId: {state.configId || 0}
                            </Typography>

                            <Typography sx={{ color: state.color || 'black' }}>
                                Estado: {state.estado || 'Sin cambios'}
                            </Typography>
                        </Paper>
                    </Grid>


                    {/* BOTONES */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancel}
                                    disabled={!formDirty}
                                >
                                    CANCELAR
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSend}
                                    disabled={jsonNeedsUpdate}
                                >
                                    ENVIAR
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>

                </Grid>
            </Paper>
        </Container>
    );
}

export default ConfiguracionAccesoForm;
